'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import Link from '@eliadmin/core/src/components/link';
import { useId, useState } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { useCurrentStore } from '../../utils/use-store-utils';
import type { Control } from 'react-hook-form';

const interests = ['Testovaciu jazdu', 'Požičanie motocykla', 'Servis', 'Iné'] as string[];

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    custom: z.object({
      interested_in: z.string().optional(),
    }),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

function ContactInput({
  control,
  name,
  label,
  placeholder,
  type,
  autoComplete,
  wrapper,
}: {
  control: Control<ContactFormValues>;
  name: 'name' | 'email' | 'phone';
  label: string;
  placeholder: string;
  type?: 'email' | 'tel';
  autoComplete: string;
  wrapper: string;
}) {
  return (
    <div className={wrapper}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem required className="p-[1px]">
            <FormLabel className="sr-only">{label}</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={placeholder}
                type={type}
                autoComplete={autoComplete}
                aria-required
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export function LandingPageContactForm() {
  const interestId = useId();
  const [interesedIn, setInterestedIn] = useState<string>(interests[0]);

  const { t } = useTranslations();
  const { isGasGas, isCFMoto } = useCurrentStore();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      custom: {
        interested_in: interesedIn,
      },
      text: '',
      gdpr: true,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await mutateAsync({
        data: {
          ...data,
        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.THANK_YOU());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'relative flex w-full max-w-[580px] flex-col items-start justify-start gap-8 bg-black p-6 sm:gap-10 sm:p-8 lg:gap-12 lg:p-10 xl:p-12',
          'ea-landing-page__contact-form',
        )}
      >
        <Image
          src={
            isCFMoto
              ? '/images/top-right-corner-blue.svg'
              : isGasGas
                ? '/images/top-right-corner-red.svg'
                : '/images/top-right-corner.svg'
          }
          alt="Banner"
          width={0}
          height={0}
          className="absolute right-0 top-0 z-20 h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12"
        />
        <h3 className="text-3xl font-medium text-white sm:text-4xl lg:text-5xl">Napíšte nám</h3>
        <div
          className={cn(
            'grid w-full grid-cols-2 gap-4 sm:gap-5 lg:gap-6',
            'ea-landing-page__contact-form__fields-wrapper',
          )}
        >
          <ContactInput
            control={form.control}
            name="name"
            label="Meno / názov spoločnosti"
            placeholder="Meno / názov spoločnosti*"
            autoComplete="name"
            wrapper="col-span-2 w-full"
          />
          <ContactInput
            control={form.control}
            name="email"
            label="Email"
            placeholder="Email*"
            type="email"
            autoComplete="email"
            wrapper="col-span-2 w-full sm:col-span-1"
          />
          <ContactInput
            control={form.control}
            name="phone"
            label="Telefón"
            placeholder="Telefón*"
            type="tel"
            autoComplete="tel"
            wrapper="col-span-2 w-full sm:col-span-1"
          />
          <div className="col-span-2 flex w-full flex-col items-start justify-start gap-2">
            <span
              id={`${interestId}-label`}
              className="text-sm font-semibold tracking-tight text-[#AAAAAA] sm:text-base"
            >
              Mám záujem o:
            </span>
            <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row sm:flex-wrap sm:gap-3 lg:gap-4">
              <FormField
                control={form.control}
                name="custom.interested_in"
                render={({ field }) => (
                  <RadioGroup
                    name={field.name}
                    aria-labelledby={`${interestId}-label`}
                    value={field.value}
                    onValueChange={value => {
                      field.onChange(value);
                      setInterestedIn(value);
                    }}
                    className="flex flex-col items-start justify-start gap-2 sm:flex-row sm:flex-wrap sm:gap-3 lg:gap-4"
                  >
                    {interests.map((interest, index) => (
                      <div key={interest} className="flex items-center justify-start gap-2.5">
                        <RadioGroupItem
                          value={interest}
                          id={`${interestId}-${index}`}
                          className={cn(
                            'min-h-6 min-w-6 rounded-full border border-white/20 bg-white/20 data-[state=checked]:bg-white/20 lg:min-h-8 lg:min-w-8',
                            field.value === interest && 'border-white bg-transparent',
                          )}
                        />
                        <label
                          htmlFor={`${interestId}-${index}`}
                          className="cursor-pointer text-sm font-medium text-white lg:text-base"
                        >
                          {interest}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>
          </div>
          <FormField
            control={form.control}
            name={'text'}
            render={({ field }) => (
              <FormItem
                required
                className={cn(
                  'col-span-2',
                  'ea-landing-page__contact-form__field ea-landing-page__contact-form__text-field',
                )}
              >
                {/* <FormLabel>
                <span>{t('text80')}</span>
              </FormLabel> */}
                <div
                  className={cn(
                    'flex w-full flex-row items-center space-x-2',
                    'ea-lading-page__contact-form__input-wrapper ea-lading-page__contact-form__text-input-wrapper',
                  )}
                >
                  <FormControl>
                    <Textarea
                      placeholder={t('text615')}
                      aria-label={t('text615')}
                      {...(field as any)}
                      rows={5}
                      rounded={CONTACT_CONFIG.form.input.rounded}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12', 'hidden')}>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full flex-col items-start justify-start gap-4 sm:flex-row sm:justify-between lg:gap-8 xl:gap-12">
          <p className="text-xs text-white md:text-sm">
            {t('text2341')}{' '}
            <Link
              prefetch={false}
              href={'/ochrana-osobnych-udajov'}
              className="font-semibold text-primary"
            >
              {t('text614')}
            </Link>{' '}
            pre účely marketingu a kontaktovania.
          </p>
          <Button
            type="submit"
            className={cn(
              'w-full whitespace-nowrap sm:w-fit',
              'ea-contact-form__submit',
              isGasGas && '!text-white',
              isCFMoto && '!text-white',
            )}
            isLoading={form.formState.isSubmitting}
          >
            Odoslať
          </Button>
        </div>
      </form>
    </Form>
  );
}
